import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  limit as fbLimit,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../firebase';
import { forgetTextureDoc, readTextureDocOnce } from './textureDocCache';

const COLLECTION_NAME = 'unifiedTextures';

// the texture documents of a read, the metadata document left out
const texturesOf = (snapshot) => {
  const textures = [];
  snapshot.forEach((doc) => {
    if (doc.id === '_metadata') return;
    textures.push({ id: doc.id, ...doc.data() });
  });
  return textures;
};

/**
 * Fetch textures from the unified collection
 *
 * @param {string} applicationType - Optional filter: 'floor', 'wall', or null for all
 * @param {{max?: number}} [opts] - `max` caps the read, for a carousel that
 *   only shows a handful. The wall catalog is ~141 documents and the panel
 *   shows eight, so filling a row has never been a reason to download the lot;
 *   See All is what fetches everything, on demand.
 */
export const getTextures = async (applicationType = null, opts = {}) => {
  try {
    const { max } = opts;
    const clauses = [
      ...(applicationType
        ? [where('applicationType', 'array-contains', applicationType)]
        : []),
      ...(Number.isFinite(max) && max > 0 ? [fbLimit(max)] : []),
    ];
    const q = clauses.length
      ? query(collection(db, COLLECTION_NAME), ...clauses)
      : collection(db, COLLECTION_NAME);

    return texturesOf(await getDocs(q));
  } catch (error) {
    console.error('Error fetching textures:', error);
    return [];
  }
};

let catalogPromise = null;

/**
 * Every texture, read ONCE per page session and shared by every caller —
 * for a reader that wants the whole list and can live with it as it was
 * first read: the estimate's price book search, which read all of it
 * (0.8 MB, ~0.6 s) on every estimate opened (2026-10-16). A texture added,
 * changed or deleted here is read again next time.
 *
 * Unlike `getTextures`, a failed read THROWS, and is not kept, so the next
 * call asks again: an empty list would read as a catalog with no finishes.
 */
export const loadTextureCatalog = () => {
  if (!catalogPromise) {
    catalogPromise = getDocs(collection(db, COLLECTION_NAME))
      .then(texturesOf)
      .catch((error) => {
        catalogPromise = null;
        throw error;
      });
  }
  return catalogPromise;
};

const forgetCatalog = () => {
  catalogPromise = null;
};

/**
 * How many textures carry an applicationType, without downloading them.
 *
 * The Fixtures & Finishes panel needs the SIZE of a surface catalog to decide
 * whether it's small enough to show inline. Asking by fetching would defeat
 * the point — wall has 141 documents and the whole reason to ask is to avoid
 * pulling them on every panel open. An aggregate count is one read.
 *
 * Throws on failure rather than returning 0: a caller that treats a failed
 * count as "empty catalog" would conclude the carousel is complete and hide
 * the only route to the rest of it.
 */
export const countTextures = async (applicationType) => {
  const q = applicationType
    ? query(
        collection(db, COLLECTION_NAME),
        where('applicationType', 'array-contains', applicationType)
      )
    : collection(db, COLLECTION_NAME);
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

/**
 * Fetch floor textures only
 */
export const getFloorTextures = async () => {
  return getTextures('floor');
};

/**
 * Fetch wall textures only
 */
export const getWallTextures = async () => {
  return getTextures('wall');
};

/**
 * Search textures by search terms
 */
export const searchTextures = async (searchTerm, applicationType = null) => {
  try {
    const searchLower = searchTerm.toLowerCase();
    let q;

    if (applicationType) {
      q = query(
        collection(db, COLLECTION_NAME),
        where('searchTerms', 'array-contains', searchLower),
        where('applicationType', 'array-contains', applicationType)
      );
    } else {
      q = query(
        collection(db, COLLECTION_NAME),
        where('searchTerms', 'array-contains', searchLower)
      );
    }

    const querySnapshot = await getDocs(q);
    const textures = [];
    querySnapshot.forEach((doc) => {
      if (doc.id === '_metadata') return;
      textures.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return textures;
  } catch (error) {
    console.error('Error searching textures:', error);
    return [];
  }
};

/**
 * Fetch a single texture by ID
 */
export const getTextureById = async (id) => {
  if (!id) return null;
  return readTextureDocOnce('unified', id, () => readTextureById(id));
};

const readTextureById = async (id) => {
  try {
    const textureRef = doc(db, COLLECTION_NAME, id);
    const textureDoc = await getDoc(textureRef);

    if (textureDoc.exists()) {
      return {
        id: textureDoc.id,
        ...textureDoc.data(),
      };
    } else {
      console.warn(`Texture with ID ${id} not found`);
      return null;
    }
  } catch (error) {
    console.error('Error fetching texture by ID:', error);
    return null;
  }
};

/**
 * Add a new texture to the unified collection
 */
export const addTexture = async (textureData) => {
  try {
    // Ensure required fields
    const enhancedData = {
      ...textureData,
      applicationType: textureData.applicationType || ['floor'],
      primaryApplication:
        textureData.primaryApplication ||
        textureData.applicationType?.[0] ||
        'floor',
      applicationMetadata: textureData.applicationMetadata || {},
      searchTerms: generateSearchTerms(textureData),
      tags: textureData.tags || [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), enhancedData);
    forgetCatalog();
    return { id: docRef.id, ...enhancedData };
  } catch (error) {
    console.error('Error adding texture:', error);
    throw error;
  }
};

/**
 * Update an existing texture
 */
export const updateTexture = async (id, updates) => {
  try {
    const textureRef = doc(db, COLLECTION_NAME, id);

    // Regenerate search terms if name, type, or manufacturer changes
    if (updates.name || updates.type || updates.manufacturer) {
      const currentDoc = await getDoc(textureRef);
      const currentData = currentDoc.data();
      updates.searchTerms = generateSearchTerms({ ...currentData, ...updates });
    }

    await updateDoc(textureRef, {
      ...updates,
      updatedAt: new Date(),
    });
    forgetCatalog();
    forgetTextureDoc(id);
    return true;
  } catch (error) {
    console.error('Error updating texture:', error);
    throw error;
  }
};

/**
 * Delete a texture
 */
export const deleteTexture = async (id) => {
  try {
    const textureRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(textureRef);
    forgetCatalog();
    forgetTextureDoc(id);
    return true;
  } catch (error) {
    console.error('Error deleting texture:', error);
    throw error;
  }
};

/**
 * Get textures suitable for a specific room type
 */
export const getTexturesForRoom = async (roomType) => {
  try {
    // Map room types to best uses
    const roomMappings = {
      bathroom: ['Bathrooms', 'Wet areas', 'Master bathrooms'],
      kitchen: ['Kitchens', 'Commercial kitchens', 'Wet areas'],
      bedroom: ['Bedrooms', 'Master bedrooms', 'Sound dampening'],
      livingroom: ['Living rooms', 'Entertainment areas', 'Sound dampening'],
      office: ['Offices', 'Commercial spaces', 'Sound dampening'],
      hallway: ['High traffic areas', 'Entryways', 'Corridors'],
      garage: ['Garages', 'Industrial', 'Heavy duty'],
    };

    const bestUses = roomMappings[roomType.toLowerCase()] || ['General use'];

    // Query for textures with matching best uses
    const q = query(
      collection(db, COLLECTION_NAME),
      where('bestFor', 'array-contains-any', bestUses)
    );

    const querySnapshot = await getDocs(q);
    const textures = [];
    querySnapshot.forEach((doc) => {
      if (doc.id === '_metadata') return;
      textures.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return textures;
  } catch (error) {
    console.error('Error fetching textures for room:', error);
    return [];
  }
};

/**
 * Get collection metadata
 */
export const getTextureMetadata = async () => {
  try {
    const metadataRef = doc(db, COLLECTION_NAME, '_metadata');
    const metadataDoc = await getDoc(metadataRef);

    if (metadataDoc.exists()) {
      return metadataDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error fetching texture metadata:', error);
    return null;
  }
};

// Helper function to generate search terms
function generateSearchTerms(textureData) {
  const terms = new Set();

  // Add name parts
  if (textureData.name) {
    textureData.name
      .toLowerCase()
      .split(/[\s_-]+/)
      .forEach((term) => {
        if (term.length > 2) terms.add(term);
      });
  }

  // Add type (construction primitive: tile / slab / panel / wood / vinyl /
  // carpet / wallpaper). `category` was never populated on any doc — see
  // the 2026-05-29 catalog audit.
  if (textureData.type) {
    terms.add(textureData.type.toLowerCase());
  }
  if (textureData.material) {
    terms.add(textureData.material.toLowerCase());
  }

  // Add manufacturer
  if (textureData.manufacturer) {
    terms.add(textureData.manufacturer.toLowerCase());
  }

  // Add application types
  if (textureData.applicationType) {
    textureData.applicationType.forEach((type) =>
      terms.add(type.toLowerCase())
    );
  }

  // Add tags
  if (textureData.tags) {
    textureData.tags.forEach((tag) => terms.add(tag.toLowerCase()));
  }

  // Add best for terms
  if (textureData.bestFor) {
    textureData.bestFor.forEach((use) => {
      use
        .toLowerCase()
        .split(/\s+/)
        .forEach((term) => {
          if (term.length > 2) terms.add(term);
        });
    });
  }

  return Array.from(terms);
}

// Export all functions
export default {
  getTextures,
  getFloorTextures,
  getWallTextures,
  searchTextures,
  getTextureById,
  countTextures,
  addTexture,
  updateTexture,
  deleteTexture,
  getTexturesForRoom,
  getTextureMetadata,
};
